import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { shallowEqual } from "react-redux";
import {
  getMainSalesPlanStart,
  createSalesPlanStart,
  updateSalesPlanStart,
  getSalesPlanStart,
} from "../../../Store/Slices/salesPlanSlice";
import { getTerritoryStart } from "../../../Store/Slices/territorySlice";
import { Utils } from "../../../Utils";

export default function TargetTree() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const salesModalData = useMemo(
    () => location.state?.salesPlanData || {},
    [location.state]
  );
  const salesPlanId = location.state?.salesPlanId;
  const isNewPlan = location.state?.isNewPlan;
  let currentCurrencyData = useSelector((state) => state?.currency)?.data?.[0];
  console.log('currentCurrencyData', Utils.currencySymbols(currentCurrencyData?.currency_code))

  const { salesPlanData, targetPlan, error, loading } = useSelector(
    (state) => ({
      salesPlanData: state.salesPlan.salesPlanData || [],
      targetPlan:
        state.salesPlan.salesPlanData?.find(
          (plan) => plan._id === salesPlanId
        ) || null,
      error: state.salesPlan.error || null,
      loading: state.salesPlan.loading || false,
    }),
    shallowEqual
  );
  const territoryList = useSelector(
    (state) => state.territory.territoryList || [],
    shallowEqual
  );

  const filteredTargetPlan = useMemo(() => {
    if (!targetPlan || typeof targetPlan !== "object") return {};
    return {
      sales_plan_name: targetPlan.data?.sales_plan_name || "",
      sales_plan_period: targetPlan.data?.sales_plan_period || "",
      sales_plan_target: targetPlan.data?.sales_plan_target || "0",
      sales_plan_option: targetPlan.data?.sales_plan_option || "existing",
      sales_plan_type: targetPlan.data?.sales_plan_type || "revenue_based",
      sales_plan_period_type:
        targetPlan.data?.sales_plan_period_type || "Jan - Dec",
      sales_plan_calculation:
        targetPlan.data?.sales_plan_calculation || "monthly",
      main_sales_plan_id: targetPlan.data?.main_sales_plan_id || targetPlan._id,
      target_data: targetPlan.data?.target_data || [],
      calculation_method: targetPlan.data?.calculation_method || "top-down",
      _id: targetPlan._id,
    };
  }, [targetPlan]);

  const [topDown, setTopDown] = useState(
    salesModalData.calculation_method
      ? salesModalData.calculation_method === "top-down"
      : true
  );
  const [territorySelfTeamState, setTerritorySelfTeamState] = useState({});
  const [data, setData] = useState({
    id: "",
    name: "",
    type: "territory",
    sales_plan_name: "",
    period: "",
    target: "0",
    target_value: "",
    secondary_value: "",
    target_split: "self",
    expanded: true,
    children: [],
  });
  const [showMismatchPopup, setShowMismatchPopup] = useState(false);

  useEffect(() => {
    dispatch(getTerritoryStart());
  }, [dispatch]);

  useEffect(() => {
    if (salesPlanId) {
      const timer = setTimeout(() => {
        dispatch(getSalesPlanStart());
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [dispatch, salesPlanId]);

  useEffect(() => {
    const targetValue = String(
      salesModalData?.sales_plan_target ||
      filteredTargetPlan?.sales_plan_target ||
      "0"
    );
    const periodValue =
      salesModalData?.sales_plan_period ||
      filteredTargetPlan?.sales_plan_period ||
      "";
    const nameValue =
      salesModalData?.sales_plan_name ||
      filteredTargetPlan?.sales_plan_name ||
      "";
    const firstTerritory = territoryList.find((t) => !t.parent_territory);
    const firstTerritoryName = firstTerritory
      ? `${firstTerritory.territory_name}: ${firstTerritory.territory_manager?.name || "No Manager"
      }`
      : "";
    const firstTerritoryId = firstTerritory?._id || "";
    const normalizedTarget = targetValue
      ? Math.floor(parseFloat(targetValue)).toString()
      : "0";

    setData((prev) => {
      if (
        prev.id === firstTerritoryId &&
        prev.name === firstTerritoryName &&
        prev.target === normalizedTarget &&
        prev.target_value === normalizedTarget &&
        prev.period === periodValue &&
        prev.sales_plan_name === nameValue &&
        prev.secondary_value ===
        (topDown ? normalizedTarget : prev.secondary_value)
      ) {
        return prev;
      }
      return {
        ...prev,
        id: firstTerritoryId,
        name: firstTerritoryName,
        target: normalizedTarget,
        target_value: normalizedTarget,
        secondary_value: topDown ? normalizedTarget : prev.secondary_value,
        period: periodValue,
        sales_plan_name: nameValue,
      };
    });
  }, [filteredTargetPlan, salesModalData, territoryList, topDown]);

  useEffect(() => {
    let children = [];
    const targetData = isNewPlan
      ? []
      : salesModalData?.target_data?.length > 0
        ? salesModalData.target_data
        : filteredTargetPlan?.target_data || [];

    const processTerritory = (territory) => ({
      id: territory.id,
      name: territory.name,
      type: "territory",
      target_value: territory.target_value
        ? Math.floor(parseFloat(territory.target_value)).toString()
        : "",
      secondary_value: territory.secondary_value
        ? Math.floor(parseFloat(territory.secondary_value)).toString()
        : "",
      target_split: territory.target_split || "self",
      expanded: true,
      children:
        territory.children?.map((child) => {
          if (child.type === "territory") {
            return processTerritory(child);
          }
          return {
            id: child.id,
            name: child.name,
            type:
              child.type === "manager" || child.type === "user"
                ? "user"
                : child.type,
            child_value: child.child_value
              ? Math.floor(parseFloat(child.child_value)).toString()
              : "",
            email: child.email || "",
            phone_no: child.phone_no || "",
            profilePicture: child.profilePicture || "",
            children: [],
          };
        }) || [],
    });

    if (targetData.length > 0) {
      children = targetData.map(processTerritory);
    } else if (territoryList.length > 0) {
      children = buildTerritoryHierarchyTree(territoryList);
    }

    const newTarget = String(
      salesModalData?.sales_plan_target ||
      filteredTargetPlan?.sales_plan_target ||
      "0"
    );
    const normalizedTarget = newTarget
      ? Math.floor(parseFloat(newTarget)).toString()
      : "0";

    setData((prev) => {
      if (
        JSON.stringify(prev.children) === JSON.stringify(children) &&
        prev.target === normalizedTarget
      ) {
        return prev;
      }
      return {
        ...prev,
        children,
        target: normalizedTarget,
      };
    });
  }, [
    filteredTargetPlan,
    salesModalData,
    territoryList,
    salesPlanId,
    isNewPlan,
  ]);

  useEffect(() => {
    let territoryStates = {};
    const targetData = isNewPlan
      ? []
      : salesModalData?.target_data?.length > 0
        ? salesModalData.target_data
        : filteredTargetPlan?.target_data || [];

    const extractTargetSplits = (nodes) => {
      nodes.forEach((territory) => {
        if (territory.type === "territory") {
          territoryStates[territory.id] = territory.target_split || "self";
          if (territory.children?.length > 0) {
            extractTargetSplits(territory.children);
          }
        }
      });
    };

    if (targetData.length > 0) {
      extractTargetSplits(targetData);
    } else {
      territoryList.forEach((territory) => {
        territoryStates[territory._id] = "self";
      });
    }

    setTerritorySelfTeamState((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(territoryStates)) {
        return prev;
      }
      return territoryStates;
    });
  }, [filteredTargetPlan, salesModalData, territoryList, isNewPlan]);

  const calculateCumulativeTarget = (
    node,
    excludeOwnTarget = false,
    excludeSubTerritories = false
  ) => {
    let sum = 0;
    if (
      node.type === "territory" &&
      !excludeOwnTarget &&
      (territorySelfTeamState[node.id] || "self") !== "team"
    ) {
      const managerTarget = parseFloat(node.target_value) || 0;
      sum += managerTarget;
      console.log(
        `Adding manager's target_value: ${managerTarget} for ${node.name}`
      );
    }
    if (node.children?.length) {
      sum += node.children.reduce((acc, child) => {
        if (child.type === "user") {
          const childValue = parseFloat(child.child_value) || 0;
          console.log(`Adding user ${child.name}'s child_value: ${childValue}`);
          return acc + childValue;
        }
        if (child.type === "territory" && !excludeSubTerritories) {
          const childTarget = parseFloat(calculateCumulativeTarget(child)) || 0;
          console.log(
            `Adding territory ${child.name}'s cumulative target: ${childTarget}`
          );
          return acc + childTarget;
        }
        return acc;
      }, 0);
    }
    return sum ? Math.floor(sum) : "";
  };

  const distributeTargetTopDown = (nodes, remainingTarget, parentId = null) => {
    return nodes.map((node) => {
      if (node.type !== "territory") {
        return {
          ...node,
          children: node.children
            ? distributeTargetTopDown(node.children, remainingTarget, parentId)
            : [],
        };
      }
      const isParentTerritory = !territoryList.find((t) => t._id === node.id)
        ?.parent_territory;
      const targetSplit = territorySelfTeamState[node.id] || "self";
      let childTargetValue = "";

      if (isParentTerritory && topDown) {
        childTargetValue = Math.floor(
          parseFloat(data.target) -
          (parseFloat(node.target_value) || 0) -
          (parseFloat(calculateCumulativeTarget(node, true, true)) || 0)
        ).toString();
      } else if (topDown && parentId !== node.id) {
        if (targetSplit === "self") {
          childTargetValue = Math.floor(
            parseFloat(remainingTarget) -
            (parseFloat(node.target_value) || 0) -
            (parseFloat(calculateCumulativeTarget(node, true, true)) || 0)
          ).toString();
        } else {
          childTargetValue = Math.floor(
            parseFloat(remainingTarget) -
            (parseFloat(calculateCumulativeTarget(node, true, true)) || 0)
          ).toString();
        }
      }
      return {
        ...node,
        secondary_value: isParentTerritory
          ? (parseFloat(data.target) || 0).toString()
          : (parseFloat(remainingTarget) || 0).toString(),
        children: node.children
          ? distributeTargetTopDown(
            node.children,
            parseFloat(childTargetValue) || 0,
            node.id
          )
          : [],
      };
    });
  };

  const calculateBottomUp = (nodes) => {
    return nodes.map((node) => {
      if (node.type !== "territory") {
        return {
          ...node,
          children: node.children ? calculateBottomUp(node.children) : [],
        };
      }
      const isParentTerritory = !territoryList.find((t) => t._id === node.id)
        ?.parent_territory;
      let cumulativeTarget = calculateCumulativeTarget(node, isParentTerritory);
      if (isParentTerritory && !topDown) {
        const parentTarget =
          (territorySelfTeamState[node.id] || "self") !== "team"
            ? parseFloat(node.target_value) || 0
            : 0;
        cumulativeTarget = (parseFloat(cumulativeTarget) || 0) + parentTarget;
      }
      return {
        ...node,
        secondary_value:
          cumulativeTarget !== ""
            ? Math.floor(cumulativeTarget).toString()
            : "",
        children: node.children ? calculateBottomUp(node.children) : [],
      };
    });
  };

  const buildTerritoryHierarchyTree = (territories) => {
    if (!Array.isArray(territories)) territories = [];
    const territoryMap = new Map();
    const roots = [];
    const managerIds = new Set(
      territories
        .filter((t) => t.territory_manager?.id)
        .map((t) => t.territory_manager.id)
    );
    territories.forEach((territory) => {
      const territoryData = {
        id: territory._id,
        name: `${territory.territory_name}: ${territory.territory_manager?.name || "No Manager"
          }`,
        type: "territory",
        target_value: "",
        secondary_value: "",
        target_split: "self",
        expanded: true,
        children: [],
      };
      if (territory.assign_users?.length > 0) {
        const userNodes = territory.assign_users
          .filter((assignedUser) => !managerIds.has(assignedUser.value))
          .map((assignedUser) => ({
            id: assignedUser.value,
            name: assignedUser.label || "Unknown User",
            type: "user",
            child_value: "",
            email: "",
            phone_no: "",
            profilePicture: "",
            expanded: false,
            children: [],
          }));
        territoryData.children.push(...userNodes);
      }
      territoryMap.set(territory._id, territoryData);
    });
    territories.forEach((t) => {
      const node = territoryMap.get(t._id);
      const parentId = t.parent_territory?.id;
      if (!parentId) {
        roots.push(node);
      } else if (parentId && territoryMap.has(parentId)) {
        territoryMap.get(parentId).children.push(node);
      }
    });
    return roots;
  };

  const formatNumber = (value) => {
    if (value === "" || value === null || value === undefined || value === 0)
      return "";
    const num = parseFloat(value);
    return isNaN(num) ? "" : Math.floor(num).toString();
  };

  const toggleExpand = (nodeId, nodes) =>
    nodes.map((node) => {
      if (node.id === nodeId) return { ...node, expanded: !node.expanded };
      if (node.children?.length)
        return { ...node, children: toggleExpand(nodeId, node.children) };
      return node;
    });

  const handleToggle = (nodeId) => {
    setData({ ...data, children: toggleExpand(nodeId, data.children) });
  };

  const updateChildValue = (nodeId, value, nodes) =>
    nodes.map((node) => {
      if (node.id === nodeId && node.type === "user")
        return { ...node, child_value: formatNumber(value) };
      if (node.id === nodeId && node.type === "territory")
        return { ...node, target_value: formatNumber(value) };
      if (node.children?.length)
        return {
          ...node,
          children: updateChildValue(nodeId, value, node.children),
        };
      return node;
    });

  const resetChildrenValues = (nodes) =>
    nodes.map((node) => ({
      ...node,
      child_value: node.type === "user" ? "" : undefined,
      target_value: node.type === "territory" ? "" : undefined,
      secondary_value: node.type === "territory" ? "" : undefined,
      children: node.children?.length ? resetChildrenValues(node.children) : [],
    }));

  const handleValueChange = (nodeId, value) => {
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    const parsedValue =
      value === "" ? "" : Math.floor(parseFloat(value)).toString();
    console.log(`Value change for node ${nodeId}: ${parsedValue}`);
    setData((prev) => {
      let newData = { ...prev };
      const isParentTerritory = prev.children.find(
        (node) =>
          node.id === nodeId &&
          !territoryList.find((t) => t._id === node.id)?.parent_territory
      );
      if (nodeId === data.id || isParentTerritory) {
        console.log(
          `Parent territory ${nodeId} target_value updated to: ${parsedValue}`
        );
        const remainingTarget = topDown
          ? Math.floor(
            parseFloat(prev.target) - parseFloat(parsedValue || "0")
          ).toString()
          : parsedValue;
        newData = {
          ...newData,
          ...(nodeId === prev.id ? { target_value: parsedValue } : {}),
          children: prev.children.map((node) => {
            if (node.id === nodeId) {
              return {
                ...node,
                target_value: parsedValue,
                secondary_value: topDown
                  ? (parseFloat(data.target) || 0).toString()
                  : calculateCumulativeTarget({
                    ...node,
                    target_value: parsedValue,
                  }).toString(),
              };
            }
            return node;
          }),
        };
        if (topDown) {
          newData.children = distributeTargetTopDown(
            newData.children,
            parseFloat(remainingTarget) || 0,
            nodeId
          );
        } else {
          newData.children = calculateBottomUp(newData.children);
        }
      } else {
        newData = {
          ...newData,
          children: updateChildValue(nodeId, parsedValue, prev.children),
        };
        if (!topDown) {
          newData.children = calculateBottomUp(newData.children);
        }
      }
      return newData;
    });
  };

  const handleSelfTeamToggle = (territoryId) => {
    setTerritorySelfTeamState((prev) => {
      const newValue = prev[territoryId] === "self" ? "team" : "self";
      console.log(`Toggling ${territoryId} to ${newValue}`);
      setData((prevData) => {
        const updatedChildren = prevData.children.map((territory) => {
          if (territory.id === territoryId) {
            const updatedTerritory = { ...territory, target_split: newValue };
            if (newValue === "team") {
              updatedTerritory.target_value = "";
              updatedTerritory.secondary_value = "";
              updatedTerritory.children = resetChildrenValues(
                territory.children
              );
            }
            return updatedTerritory;
          }
          return territory;
        });
        return { ...prevData, children: updatedChildren };
      });
      return { ...prev, [territoryId]: newValue };
    });
  };

  const updatedChildren = useMemo(() => {
    const updateTerritoryValues = (
      nodes,
      parentSecondaryValue = data.target
    ) => {
      return nodes.map((node) => {
        if (node.type !== "territory") {
          return {
            ...node,
            children: node.children
              ? updateTerritoryValues(node.children, parentSecondaryValue)
              : [],
          };
        }
        const targetSplit = territorySelfTeamState[node.id] || "self";
        let newSecondaryValue = "";
        let childTargetValue = "";
        if (topDown && !isNewPlan) {
          newSecondaryValue = node.secondary_value || "";
          childTargetValue = "";
        } else if (topDown) {
          const isParentTerritory = !territoryList.find(
            (t) => t._id === node.id
          )?.parent_territory;
          if (isParentTerritory) {
            newSecondaryValue = (parseFloat(data.target) || 0).toString();
            childTargetValue = Math.floor(
              parseFloat(data.target) -
              (parseFloat(node.target_value) || 0) -
              (parseFloat(calculateCumulativeTarget(node, true, true)) || 0)
            ).toString();
          } else {
            newSecondaryValue = (
              parseFloat(parentSecondaryValue) || 0
            ).toString();
            if (targetSplit === "self") {
              childTargetValue = Math.floor(
                parseFloat(parentSecondaryValue) -
                (parseFloat(node.target_value) || 0) -
                (parseFloat(calculateCumulativeTarget(node, true, true)) || 0)
              ).toString();
            } else {
              childTargetValue = Math.floor(
                parseFloat(parentSecondaryValue) -
                (parseFloat(calculateCumulativeTarget(node, true, true)) || 0)
              ).toString();
            }
          }
        } else {
          const isParentTerritory = !territoryList.find(
            (t) => t._id === node.id
          )?.parent_territory;
          newSecondaryValue = calculateCumulativeTarget(
            node,
            isParentTerritory
          ).toString();
          if (isParentTerritory && targetSplit !== "team") {
            const parentTarget = parseFloat(node.target_value) || 0;
            newSecondaryValue = Math.floor(
              (parseFloat(newSecondaryValue) || 0) + parentTarget
            ).toString();
          }
        }
        return {
          ...node,
          secondary_value:
            newSecondaryValue >= 0
              ? Math.floor(parseFloat(newSecondaryValue)).toString()
              : "",
          children: node.children
            ? updateTerritoryValues(node.children, childTargetValue)
            : [],
        };
      });
    };
    return updateTerritoryValues(data.children);
  }, [data.children, territorySelfTeamState, topDown, data.target, isNewPlan]);

  const currencySymbol = Utils.currencySymbols(currentCurrencyData?.currency_code) || '$';

  const renderNode = (node, level = 0, isLastSibling = true) => {
    const isTerritory = node.type === "territory";
    const isUser = node.type === "user";
    const targetSplit = territorySelfTeamState[node.id] || "self";
    const displayValue = isTerritory
      ? targetSplit === "team"
        ? ""
        : formatNumber(node.target_value) || ""
      : isUser
        ? formatNumber(node.child_value)
        : formatNumber(node.target);

    const secondBoxValue = isTerritory
      ? formatNumber(node.secondary_value)
      : "";

    const verticalLineStyle = {
      position: "absolute",
      top: 0,
      bottom: 0,
      left: `${level * 20 - 10}px`,
      width: "1px",
      backgroundColor: "#ccc",
      zIndex: 0,
    };

    const horizontalLineStyle = {
      position: "absolute",
      top: "50%",
      left: `${level * 20 - 10}px`,
      width: "10px",
      height: "1px",
      backgroundColor: "#ccc",
      zIndex: 0,
    };

    return (
      <div key={node.id} style={{ position: "relative" }}>
        {level > 0 && (
          <>
            <div style={verticalLineStyle} />
            <div style={horizontalLineStyle} />
          </>
        )}
        <div
          className="d-flex align-items-center mb-2"
          style={{ minWidth: "750px", position: "relative", zIndex: 1 }}
        >
          <div
            className="position-relative"
            style={{
              width: "300px",
              paddingLeft: `${level * 20}px`,
              display: "flex",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            {node.children?.length > 0 ? (
              <button
                onClick={() => handleToggle(node.id)}
                className="btn btn-sm btn-outline-secondary px-2 py-0 me-2"
                style={{ width: "30px", height: "30px", zIndex: 2 }}
                aria-label={node.expanded ? "Collapse" : "Expand"}
              >
                {node.expanded ? "−" : "+"}
              </button>
            ) : (
              <div style={{ width: "38px" }} />
            )}
            <span>{node.name}</span>
          </div>
          <div
            className="d-flex align-items-center gap-2 flex-grow-1"
            style={{ zIndex: 1 }}
          >
            <div
              className="input-group input-group-sm"
              style={{ width: "120px" }}
            >
              <span className="input-group-text px-2">{currencySymbol}</span>
              <input
                type="text"
                className="form-control"
                value={displayValue}
                readOnly={isTerritory && targetSplit === "team"}
                onChange={(e) => handleValueChange(node.id, e.target.value)}
                style={{ zIndex: 1 }}
              />
            </div>
            {isTerritory && (
              <>
                <div
                  className="input-group input-group-sm"
                  style={{ width: "120px" }}
                >
                  <span className="input-group-text px-2">{currencySymbol}</span>
                  <input
                    type="text"
                    className="form-control"
                    value={secondBoxValue}
                    readOnly
                    style={{ zIndex: 1 }}
                  />
                </div>
                <div className="d-flex align-items-center gap-2 ms-2">
                  <label
                    className="form-check-label me-2"
                    htmlFor={`selfTeamToggle_${node.id}`}
                  >
                    Self
                  </label>
                  <div className="form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`selfTeamToggle_${node.id}`}
                      checked={targetSplit === "team"}
                      onChange={() => handleSelfTeamToggle(node.id)}
                    />
                  </div>
                  <label
                    className="form-check-label ms-2"
                    htmlFor={`selfTeamToggle_${node.id}`}
                  >
                    Team
                  </label>
                </div>
              </>
            )}
          </div>
        </div>
        {node.expanded &&
          node.children?.map((child, index) =>
            renderNode(child, level + 1, index === node.children.length - 1)
          )}
      </div>
    );
  };

  const handleSave = () => {
    const sourceData = isNewPlan
      ? salesModalData || {}
      : filteredTargetPlan || {};
    const mainSalesPlanId =
      sourceData.main_sales_plan_id || sourceData._id || salesPlanId;

    const processTerritory = (territory) => {
      const territoryData = {
        id: territory.id,
        name: territory.name.split(":")[0].trim(),
        type: "territory",
        target_value: formatNumber(territory.target_value),
        secondary_value: formatNumber(territory.secondary_value),
        target_split: territorySelfTeamState[territory.id] || "self",
        children: territory.children.map((child) => {
          if (child.type === "territory") {
            return processTerritory(child);
          }
          return {
            id: child.id,
            name: child.name,
            type: "user",
            child_value: formatNumber(child.child_value),
            email: child.email || "",
            phone_no: child.phone_no || "",
            profilePicture: child.profilePicture || "",
            children: [],
          };
        }),
      };
      console.log(`Processing territory ${territory.name}:`, territoryData);
      return territoryData;
    };

    const newSalesPlan = {
      sales_plan_name: data.sales_plan_name,
      sales_plan_period: data.period,
      sales_plan_target: data.target,
      sales_plan_option: sourceData.sales_plan_option,
      sales_plan_type: sourceData.sales_plan_type,
      sales_plan_period_type: sourceData.sales_plan_period_type,
      sales_plan_calculation: sourceData.sales_plan_calculation,
      main_sales_plan_id: mainSalesPlanId,
      target_data: updatedChildren.map(processTerritory),
      calculation_method: topDown ? "top-down" : "bottom-up",
    };

    console.log(
      "New Sales Plan Payload:",
      JSON.stringify(newSalesPlan, null, 2)
    );

    const parentTerritory = updatedChildren.find(
      (node) => !territoryList.find((t) => t._id === node.id)?.parent_territory
    );
    if (
      parentTerritory &&
      parseFloat(parentTerritory.secondary_value) !== parseFloat(data.target)
    ) {
      setShowMismatchPopup(true);
      return;
    }

    const payload = { data: newSalesPlan };
    console.log("Payload:", payload);
    if (isNewPlan) {
      dispatch(
        createSalesPlanStart({
          data: payload.data,
          id: mainSalesPlanId,
        })
      );
    } else {
      dispatch(
        updateSalesPlanStart({
          data: payload.data,
          id: salesPlanId,
        })
      );
    }
    // navigate("/digylax/form-module/sales-plan");
    navigate("/digylax/form-module/forecasting");
  };

  const handleFixMismatch = () => {
    setData((prev) => {
      const parentTerritory = prev.children.find(
        (node) =>
          !territoryList.find((t) => t._id === node.id)?.parent_territory
      );
      if (!parentTerritory) return prev;
      const targetValue = parseFloat(prev.target) || 0;
      const currentSecondary = parseFloat(parentTerritory.secondary_value) || 0;
      const difference = targetValue - currentSecondary;
      return {
        ...prev,
        children: prev.children.map((node) => {
          if (node.id === parentTerritory.id) {
            return {
              ...node,
              target_value: (
                parseFloat(node.target_value) || 0 + difference
              ).toString(),
              secondary_value: prev.target,
            };
          }
          return node;
        }),
      };
    });
    setShowMismatchPopup(false);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="container mt-4">
      <h5>Sales Plan Target</h5>
      <div className="mb-3">
        {data.sales_plan_name && (
          <h6 className="text-muted">Plan Name: {data.sales_plan_name}</h6>
        )}
        {data.period && <h6 className="text-muted">Period: {data.period}</h6>}
        {data.target && (
          <h6 className="text-muted">
            Target Set: {currencySymbol}{formatNumber(data.target)}
          </h6>
        )}
      </div>
      <div className="mb-3 d-flex align-items-center">
        <span
          className="text-dark fw-bold"
          style={{ fontSize: "14px", marginRight: "10px" }}
        >
          Top-Down
        </span>
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="topDownSwitch"
            checked={!topDown}
            onChange={() => setTopDown(!topDown)}
            style={{ width: "30px", height: "15px", marginLeft: "-10px" }}
          />
        </div>
        <span
          className="text-dark fw-bold"
          style={{ fontSize: "14px", marginLeft: "10px" }}
        >
          Bottom-Up
        </span>
      </div>
      <div
        className="d-flex justify-content-end"
        style={{ marginTop: "-30px" }}
      >
        <button className="btn btn-primary mb-3" onClick={handleSave}>
          Save Sales Plan
        </button>
      </div>
      <div
        className="border p-3 rounded"
        style={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        {data.children.length === 0 && !loading && !error && (
          <div>
            No territories available to display. Please ensure territories are
            set up correctly.
          </div>
        )}
        {updatedChildren.map((child, index) =>
          renderNode(child, 0, index === updatedChildren.length - 1)
        )}
      </div>
      {showMismatchPopup && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Target Mismatch</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowMismatchPopup(false)}
                ></button>
              </div>
              <div className="modal-body">
                The parent territory's cumulative target does not match the set
                target. Please adjust the parent territory's target value to
                proceed with saving.
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowMismatchPopup(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleFixMismatch}
                >
                  Fix
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
